import { defineStore } from 'pinia';
import { nextTick, shallowRef, watch } from 'vue';
import { BookOrder } from '@/connect/gen/modules/apiengine/services/venue/venue_pb';
import { useDialogManager } from '@/plugins/dialog-manager';

export const useOrderFormStore = defineStore('orderForm', () => {
  const orderToMatch = shallowRef<BookOrder | null>(null);
  const showSummary = shallowRef(false);

  const showForm = shallowRef(false);
  watch(showForm, () => (showSummary.value = false));

  const orderRef = shallowRef<string | null>(null);
  const shouldSplit = shallowRef<boolean>(false);
  const dialogManager = useDialogManager();

  function editOrder(order: BookOrder): void {
    confirmOrderFormReset(() => {
      showForm.value = true;
      orderToMatch.value = null;
      shouldSplit.value = false;
      orderRef.value = order.clientOrderRef;
    });
  }

  function matchOrder(order: BookOrder, shouldSplitOrder: boolean): void {
    confirmOrderFormReset(() => {
      // in case edit was ongoing, reset orderRef
      orderRef.value = null;
      orderToMatch.value = order;
      shouldSplit.value = shouldSplitOrder;
      showForm.value = true;
    });
  }

  function confirmOrderFormReset(onConfirm: () => void | Promise<void>): void {
    if (!showSummary.value) {
      void onConfirm();
      return;
    }

    dialogManager.ask({
      title: 'Current Order Will Be Lost',
      message:
        'The current order form will be reset and any unsaved changes will be lost. Do you want to continue?',
      acceptText: 'Continue',
      rejectText: 'Cancel',
      onAccept: () => {
        // revert component from summary to form and perform action
        showSummary.value = false;
        void onConfirm();
      },
    });
  }

  async function resetAndShowForm(): Promise<void> {
    // force a re-render
    showForm.value = false;
    await nextTick();

    orderToMatch.value = null;
    orderRef.value = null;
    showForm.value = true;
  }

  return {
    showForm,
    orderRef,
    orderToMatch,
    editOrder,
    matchOrder,
    resetAndShowForm,
    shouldSplit,
    showSummary,
  };
});

export type OrderFormStore = ReturnType<typeof useOrderFormStore>;
